'use client';

import React, { useState } from 'react';
import type { ZodType } from 'zod';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useCustomerApi } from '@packages/shared/src/hooks/useCustomerApi/useCustomerApi';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import type { CustomerDataWithStatus } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1';
import { CircularProgress } from '@packages/shared/src/ui/CircularProgress/CircularProgress';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Card, CardHeader } from '@packages/shared/src/ui/Card/Card';
import { Checkbox } from '@packages/shared/src/ui/Checkbox/Checkbox';
import { Input } from '@packages/shared/src/ui/Input/Input';
import { Label } from '@packages/shared/src/ui/Label/Label';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogFooter } from '@packages/shared/src/ui/Dialog/DialogFooter/DialogFooter';
import { DialogHeaderBase as DialogHeader } from '@packages/shared/src/ui/Dialog/DialogHeader/DialogHeaderBase.client';

const messages = defineMessages({
  headline: {
    id: 'ChangeEmail.headline',
    defaultMessage: 'E-Mail-Adresse ändern',
  },
  inputLabel: {
    id: 'ChangeEmail.inputLabel',
    defaultMessage: 'Neue E-Mail-Adresse',
  },
  inputConfirmationLabel: {
    id: 'ChangeEmail.inputConfirmationLabel',
    defaultMessage: 'Bestätigung E-Mail-Adresse',
  },
  inputPlaceholder: {
    id: 'ChangeEmail.inputPlaceholder',
    defaultMessage: 'ihrname@gmail.com',
  },
  newsletterLabel: {
    id: 'ChangeEmail.newsletterLabel',
    defaultMessage:
      'Ja, ich möchte den {companyName} Newsletter. Nur einen Klick entfernt von aktuellen Trends, Neuheiten sowie jeder Menge Aktionen und Rabatten.',
  },
  notEqual: {
    id: 'ChangeEmail.notEqual',
    defaultMessage: 'Ihre Eingaben stimmen nicht überein',
  },
  errorNoInput: {
    id: 'ChangeEmail.errorNoInput',
    defaultMessage: 'Bitte geben Sie Ihre neue E-Mail-Adresse an',
  },
  errorEmailField: {
    id: 'Login.message.invalidMail',
    defaultMessage: 'Die E-Mail-Adresse ist nicht gültig.',
  },
  changeEmailAddress: {
    id: 'LoginContainer.ChangeEmailAddress',
    defaultMessage: 'E-Mail-Adresse ändern',
  },
  emailBounceText: {
    id: 'LoginContainer.EmailBounceText',
    defaultMessage:
      'Liebe Kundin, lieber Kunde,\n\nwir konnten leider an Ihre Adresse keine E-Mail zustellen.\n\nDamit wir Sie auch in Zukunft problemlos mit Informationen zu unserem Angebot und Ihrem Kundenkonto kontaktieren können, bitten wir Sie Ihre Adresse zu prüfen und ggf. zu aktualisieren. Hinweis: bitte prüfen Sie, ob ggf. Ihr E-Mail-Postfach voll ist!\n\nSollte es weiterhin Probleme mit Ihrer E-Mail-Adresse geben oder Sie wollen uns diese grundsätzlich nicht angeben, teilen Sie uns dies bitte gesondert an <link>service@imwalking.de</link> mit.',
  },
  contactEmail: {
    id: 'ServiceContactTile.emailAddress',
    defaultMessage: 'service@example.com',
  },
});

export const preventPaste = (
  event: React.ClipboardEvent<HTMLInputElement> | React.DragEvent<HTMLInputElement>,
) => {
  event.preventDefault();
  return false;
};

type EmailFormFieldName = 'email' | 'emailConfirmation';

type EmailFormState = Record<EmailFormFieldName, string>;

export const EmailConfirmationSchema: ZodType<EmailFormState> = z
  .object({
    email: z.string().email(messages.errorEmailField.defaultMessage),
    emailConfirmation: z.string().email(),
  })
  .superRefine(({ emailConfirmation, email }, ctx) => {
    if (emailConfirmation !== email) {
      ctx.addIssue({
        code: 'custom',
        message: messages.notEqual.defaultMessage,
        path: ['emailConfirmation'],
      });
    }
  });

export const ChangeEmailClient = ({
  hideHeadline,
  customer,
  onClose,
}: {
  hideHeadline?: boolean;
  customer: CustomerDataWithStatus | null;
  onClose?: () => void;
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const config = useConfig();
  const emailAddress =
    config.tracking.schemaOrgOrganisation[
      intl.locale as keyof typeof config.tracking.schemaOrgOrganisation
    ]?.contactPoint?.[0]?.email || '';

  const {
    register,
    watch,
    formState: { errors },
  } = useForm<EmailFormState>({ resolver: zodResolver(EmailConfirmationSchema), mode: 'onChange' });
  // TODO: remove watch
  const email = watch('email');
  // TODO: use form state
  const [loading, setLoading] = useState<boolean>(false);
  const [errorGlobal, setErrorGlobal] = useState<string>('');
  const [newsletter, setNewsletter] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(true);

  const customerApi = useCustomerApi();

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  const sendData = async () => {
    if (!errors?.email && !errors.emailConfirmation) {
      setLoading(true);
      const result = await customerApi?.changeEmailAddress({
        changeEmailAddressRequestPayload: {
          newEmailAddress: email,
          newsletter,
        },
        ecLocale: intl.locale,
      });
      if (result?.errors) {
        setErrorGlobal(result.errors.elements[0].description);
        setLoading(false);
      } else if (result?.customer) {
        setLoading(false);
      }
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={() => handleClose()} className="lg:w-[768px]">
      <DialogHeader onClose={() => handleClose()}>{formatMessage(messages.headline)}</DialogHeader>
      {loading ? (
        <div className="mx-auto flex h-[200px] min-w-[500px] items-center justify-center">
          <CircularProgress className="h-[40px] w-[40px]" />
        </div>
      ) : (
        <>
          <div className="h-full w-full">
            <div className="gap flex flex-col px-3 text-center">
              <p className="text-size-headline-2xl pt-4 pb-2">
                {formatMessage(messages.changeEmailAddress)}
              </p>
              <p className="text-size-body-sm mb-2 text-center">
                {formatMessage(messages.emailBounceText, {
                  link: () => <a href={`mailto:${emailAddress}`}>{emailAddress}</a>,
                })}
              </p>
            </div>
            {!hideHeadline && <div className="mx-4">{intl.formatMessage(messages.headline)}</div>}
            <div className="bg-color-grey-light text-size-body-sm p-2">
              {errorGlobal.length > 0 && (
                <Notification severity="error">{errorGlobal}</Notification>
              )}
              {/* TODO: use form handling submit */}
              <form>
                <Input
                  className="mb-2"
                  placeholder={intl.formatMessage(messages.inputPlaceholder)}
                  label={intl.formatMessage(messages.inputLabel)}
                  error={!!errors?.email}
                  helperText={errors?.email ? intl.formatMessage(messages.errorNoInput) : undefined}
                  {...register('email')}
                />
                <Input
                  placeholder={intl.formatMessage(messages.inputPlaceholder)}
                  label={intl.formatMessage(messages.inputConfirmationLabel)}
                  onPaste={(event) => {
                    preventPaste(event);
                  }}
                  onDrop={(event) => {
                    preventPaste(event);
                  }}
                  error={!!errors?.emailConfirmation}
                  helperText={
                    errors?.emailConfirmation ? errors.emailConfirmation?.message : undefined
                  }
                  {...register('emailConfirmation')}
                />
              </form>
              {customer?.permissions.newsletter && (
                <div className="mt-4">
                  <Notification severity="info">
                    <FormattedMessage
                      id="ChangeEmail.newsletterHint"
                      defaultMessage="Du hast bisher unseren Newsletter an deine alte E-Mail Adresse bekommen. Für die neue E-Mail Adresse benötigen wir erneut deine Zustimmung."
                    />
                  </Notification>
                </div>
              )}
              <div className="mt-4 mb-4 flex items-start gap-1" data-testid="newsletter-checkbox">
                <Checkbox name="newsletter-checkbox" onChange={() => setNewsletter(!newsletter)} />
                <Label htmlFor="newsletter-checkbox">
                  {intl.formatMessage(messages.newsletterLabel, {
                    companyName: config.company.name,
                  })}
                </Label>
              </div>

              <Card className="bg-color-grey-light p-2 text-center">
                <CardHeader className="justify-center font-bold">
                  <FormattedMessage
                    id="ChangeEmail.emailAddressHintHeadline"
                    defaultMessage="Bitte beachten Sie"
                  />
                </CardHeader>
                <FormattedMessage
                  id="ChangeEmail.emailAddressHintText"
                  defaultMessage="Ihre neue E-Mail-Adresse gilt für die Anmeldung und alle Informationen rund um Ihre Bestellung sowie Service- und Kundeninformationen."
                />
              </Card>
            </div>
          </div>
          <DialogFooter>
            <div className="mt-2 flex w-full flex-col justify-stretch">
              {/* TODO: move buttons inside the form */}
              <Button
                color="primary"
                size="large"
                type="submit"
                onClick={() => sendData()}
                data-testid="submit-button"
                className="mx-4 mb-2"
              >
                <FormattedMessage id="ChangeEmail.save" defaultMessage="Änderungen speichern" />
              </Button>
              <Button
                size="large"
                onClick={() => handleClose()}
                className="mx-4 mb-2"
                data-testid="close-post-login-email"
              >
                <FormattedMessage id="ChangeEmail.cancel" defaultMessage="abbrechen" />
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
};
